import { Dispatch, useCallback } from "react";
import {
  DisplayMenuItem,
  DisplayMenuSection,
  MenuContentAction,
  MenuContentActions,
} from "models/menu/menuContentsReducer";
import { getMenuOptionByPath } from "utils/menu/menu/menuContentDisplay";
import { isDisplayMenuItem } from "models/menu/menu";

const useMenuOptionActions = (
  menuContents: Record<string, DisplayMenuSection>,
  dispatchMenuContents: Dispatch<MenuContentAction>,
  displayMenuOptionUUIDPath: string,
) => {
  /**
   * Detect the self type of the menu option
   */
  const selfType: "menu item" | "option group" = isDisplayMenuItem(
    getMenuOptionByPath(menuContents, displayMenuOptionUUIDPath),
  )
    ? "menu item"
    : "option group";

  /**
   * Move a menu item up or down in the parent menu options
   */
  const handleMove = useCallback(
    (direction: "up" | "down") => () => {
      dispatchMenuContents({
        type:
          direction === "up"
            ? MenuContentActions.MOVE_OPTION_UP
            : MenuContentActions.MOVE_OPTION_DOWN,
        UUIDPath: displayMenuOptionUUIDPath,
      });
    },
    [dispatchMenuContents, displayMenuOptionUUIDPath],
  );

  /**
   * Remove a menu item from the menu option group (section or side option group)
   */
  const handleRemove = useCallback(() => {
    dispatchMenuContents({
      type: MenuContentActions.REMOVE_OPTION,
      UUIDPath: displayMenuOptionUUIDPath,
    });
  }, [dispatchMenuContents, displayMenuOptionUUIDPath]);

  /**
   * Add a new menu item or option group
   */
  const handleAdd = useCallback(
    (itemType: "menu item" | "option group") => () => {
      let actionType;
      switch (itemType) {
        case "menu item":
          actionType = MenuContentActions.ADD_ITEM;
          break;
        case "option group":
          actionType = MenuContentActions.ADD_OPTION_GROUP;
          break;
        default:
          actionType = MenuContentActions.ADD_ITEM;
      }

      dispatchMenuContents({
        type: actionType,
        UUIDPath: displayMenuOptionUUIDPath,
      });
    },
    [dispatchMenuContents, displayMenuOptionUUIDPath],
  );

  /**
   * Update the menu item or option group
   */
  const handleUpdate = useCallback(
    (newData: Partial<DisplayMenuSection> | Partial<DisplayMenuItem>) => {
      switch (selfType) {
        case "menu item":
          dispatchMenuContents({
            type: MenuContentActions.UPDATE_OPTION,
            UUIDPath: displayMenuOptionUUIDPath,
            newItemFields: newData as Partial<DisplayMenuItem>,
          });
          break;
        case "option group":
          dispatchMenuContents({
            type: MenuContentActions.UPDATE_OPTION,
            UUIDPath: displayMenuOptionUUIDPath,
            newOptionGroupFields: newData as Partial<DisplayMenuSection>,
          });
          break;
        default:
          break;
      }
    },
    [dispatchMenuContents, displayMenuOptionUUIDPath],
  );

  return {
    handleMove,
    handleRemove,
    handleAdd,
    handleUpdate,
  };
};

export default useMenuOptionActions;
